import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import navigateToURL from 'lib/navigateToURL';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import {ChipSize} from 'ui-library/chips/Chip';
import ChipList from 'ui-library/chips/ChipList';
import * as Colors from 'ui-library/Colors';

import {Body, Cell, Header, HeaderCell, Row, Table} from '../../../StudyTableComponent';
import {
  conditionAppliesToMethod,
  PLANNED,
  ProcedureRunOverview,
  UnitOperationExecutionInstance,
  type UnitOperationStatusType,
} from './procedureRunOverviewUtils';
import {RenderTaskStatus, RenderWorksheetChip} from './ProcedureRunTableUtil';

interface MultiProcedureRunParamsTableProps {
  overview: ProcedureRunOverview;
  studyType: StudyType;
  enabledStatusFilters?: Map<UnitOperationStatusType, boolean>;
}

export function MultiProcedureRunParamsTable({
  overview,
  studyType,
  enabledStatusFilters,
}: MultiProcedureRunParamsTableProps): JSX.Element {
  const areFiltersEnabled =
    enabledStatusFilters && Array.from(enabledStatusFilters.values()).filter((value) => value).length > 0;

  const filteredUnitOperations = overview.unitOperationExecutionInstances.filter(
    (unitOperationExecutionInstance) => {
      const unitOperationStatus = unitOperationExecutionInstance?.status?.statusType ?? PLANNED;
      return !areFiltersEnabled || enabledStatusFilters.get(unitOperationStatus);
    }
  );

  const filteredUnitOpsByMethodConditionReplicate = new Map<string, UnitOperationExecutionInstance>(
    filteredUnitOperations.map((uo) => [`${uo.methodId}-${uo.conditionId}-${uo.replicateNumber}`, uo])
  );

  return (
    <Table dataTestAttrs={{component: 'MultiProcedureRunParamsTable', element: 'table'}}>
      <Header className={css(styles.headerRowStyle)}>
        <HeaderCell className={css(styles.headerUnitOperationCell)}> Unit Operation </HeaderCell>
        <HeaderCell className={css(styles.headerConditionCell)}> Condition - Replicate </HeaderCell>
        <HeaderCell className={css(styles.headerStatusCell)}> Status </HeaderCell>
        <HeaderCell className={css(styles.sharedHeaderCell)}> Worksheet </HeaderCell>
        <HeaderCell className={css(styles.sharedHeaderCell)}>Previous unit operations</HeaderCell>
        <HeaderCell className={css(styles.sharedHeaderCell)}> Description </HeaderCell>
      </Header>
      <Body>
        {overview.unitOperationMethods.flatMap((unitOperation) =>
          // Scope each unit-op method to the conditions it actually owns: a per-method condition
          // pairs only with its owning method. Without this filter, runs would emit a row per
          // (method, condition) cross product and visibly duplicate rows under every method.
          overview.conditions
            .filter((condition) => conditionAppliesToMethod(condition, unitOperation.id))
            .flatMap((condition) =>
              condition.replicates.map((replicate) => {
                const unitOperationExecutionInstance = filteredUnitOpsByMethodConditionReplicate.get(
                  `${unitOperation.id}-${condition.id}-${replicate.replicateNumber}`
                );
                if (unitOperationExecutionInstance) {
                  return (
                    <Row
                      key={unitOperationExecutionInstance.id}
                      onClick={() => {
                        if (unitOperationExecutionInstance.worksheetUrl) {
                          navigateToURL(unitOperationExecutionInstance.worksheetUrl);
                        }
                      }}
                      className={css(styles.rowStyle)}
                    >
                      <Cell className={css(styles.unitOperationCell)}>{unitOperation.name}</Cell>
                      <Cell className={css(styles.conditionCell)}>
                        <div className={css(styles.conditionText)}>{condition.name}</div>
                        <div className={css(styles.replicateText)}>{` - ${replicate.replicateNumber}`}</div>
                      </Cell>
                      <Cell className={css(styles.statusCell)}>
                        <RenderTaskStatus
                          statusType={unitOperationExecutionInstance.status?.statusType ?? null}
                        />
                      </Cell>
                      <Cell className={css(styles.sharedCell)}>
                        <RenderWorksheetChip worksheetId={unitOperationExecutionInstance.worksheetId} />
                      </Cell>
                      <Cell className={css(styles.previousUnitOperationCell)}>
                        <ChipList
                          chipSize={ChipSize.MINI}
                          itemIds={unitOperationExecutionInstance.linkedUnitOperationExecutionInstanceIds}
                          maxChips={3}
                        />
                      </Cell>
                      <Cell className={css(styles.sharedCell)}>{condition.description}</Cell>
                    </Row>
                  );
                }
                return null;
              })
            )
        )}
      </Body>
    </Table>
  );
}

const styles = StyleSheet.create({
  conditionCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
    display: 'flex',
    alignItems: 'center',
  },
  conditionText: {
    flex: '0 1 content',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  replicateText: {
    color: Color.GRAY_500,
    marginLeft: 4,
    flex: '0 0 content',
    textAlign: 'left',
  },
  unitOperationCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  statusCell: {
    flex: '0 0 10%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  previousUnitOperationCell: {
    flex: '0 0 20%',
    alignContent: 'center',
    whiteSpace: 'nowrap',
    ...FONT_PROXIMA_13,
  },
  sharedCell: {
    flex: '1 0 20%',
    maxWidth: '30%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  expandedSharedCell: {
    flex: '0 0 30%',
    alignContent: 'center',
    ...FONT_PROXIMA_13,
  },
  rowStyle: {
    width: '100%',
    minHeight: 32,
    alignContent: 'center',
  },
  headerRowStyle: {
    height: 32,
    alignContent: 'center',
  },
  headerConditionCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    color: Colors.GRAY_SEMIDARK,
    ...FONT_PROXIMA_13,
  },
  headerUnitOperationCell: {
    flex: '0 0 15%',
    alignContent: 'center',
    color: Colors.GRAY_SEMIDARK,
    ...FONT_PROXIMA_13,
  },
  headerStatusCell: {
    flex: '0 0 10%',
    alignContent: 'center',
    color: Colors.GRAY_SEMIDARK,
    ...FONT_PROXIMA_13,
  },
  sharedHeaderCell: {
    flex: '1 0 20%',
    maxWidth: '30%',
    alignContent: 'center',
    color: Colors.GRAY_SEMIDARK,
    ...FONT_PROXIMA_13,
  },
  sharedHeaderCellExpanded: {
    flex: '0 0 30%',
    alignContent: 'center',
    color: Colors.GRAY_SEMIDARK,
    ...FONT_PROXIMA_13,
  },
});
